const PolyfilledSubmitEvent: typeof SubmitEvent = (globalThis.SubmitEvent =
    typeof globalThis.SubmitEvent !== 'undefined' ? SubmitEvent : (Event as unknown as typeof SubmitEvent));

/**
 * Implicitly submit a form by first validating all controls. If the form
 * is valid, issue a submit event and if that event is not prevented, manually
 * call the form's submit method.
 *
 * @param form {HTMLFormElement} - A form to implicitly submit
 */
export const submit = (form: HTMLFormElement): void => {
    if (!form.noValidate && !form.reportValidity()) {
        return;
    } else {
        const submitEvent = new PolyfilledSubmitEvent('submit', {
            bubbles: true,
            cancelable: true
        });
        form.dispatchEvent(submitEvent);
        if (!submitEvent.defaultPrevented) {
            form.submit();
        }
    }
};

/**
 * Parse a form and return a set of values based on the name/value pair.
 * If multiple controls of a similar name exist, return an array for those values;
 * otherwise return a single value.
 *
 * @param form {HTMLFormElement} - The form to parse for values
 * @returns {Record<string, string | string[]>} - An object representing the form's current values
 */
export const formValues = (form: HTMLFormElement): Record<string, string | string[]> => {
    const formData = new FormData(form);
    const values: Record<string, string | string[]> = {};

    formData.forEach((value, key) => {
        if (!values.hasOwnProperty(key)) {
            values[key] = value.toString();
        } else if (Array.isArray(values[key])) {
            (values[key] as string[]).push(value.toString());
        } else {
            values[key] = [values[key] as string, value.toString()];
        }
    });

    return values;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FormValue = string | string[] | Record<string, any>;

/**
 * This method takes a form and parses it as an object. If any form control has a `.`
 * in its name, this utility will evaluate that name as a deep key for an object;
 * in other words, if a form has a named control, `name.first` and another, `name.last`
 * it will report back a nested object, name, with first and last properties
 * representing those controls' values.
 *
 * This can be useful when you have a complex model that you are attempting to represent
 * in declaratively in HTML.
 *
 * @param form {HTMLFormElement} - The form to grab values from
 * @returns {Record<string, FormValue>} - An object representation of the form
 */
export const parseFormAsObject = (form: HTMLFormElement): Record<string, FormValue> => {
    const data = formValues(form);
    const output: Record<string, FormValue> = {};

    Object.entries(data).forEach(([key, value]) => {
        /** If the key has a '.', parse it as an object */
        if (key.includes('.')) {
            const path = key.split('.');
            const destination = path.pop() as string;
            let pointer: Record<string, FormValue> = output;
            while (path.length) {
                const key = path.shift() as string;
                pointer[key] = pointer[key] ?? {};
                pointer = pointer[key] as Record<string, FormValue>;
            }
            pointer[destination] = value;
        } else {
            output[key] = data[key];
        }
    });

    return output;
};
